/**
 * Shows each Range Plan upload result exactly once, in the tab the user is looking at (BM-9211).
 *
 * The server sends an upload result to every socket of the user, so every open tab — on any module —
 * receives it, and each used to pop the same popup, download the same error sheet and raise the same
 * toast. Tabs still receive every result; this router makes them agree that ONE shows it:
 *
 * - a tab that receives a result OFFERS it into a queue shared through `localStorage`;
 * - only the tab the user is looking at PRESENTS, claiming each result under a lock so two tabs can
 *   never both show it;
 * - when no tab is being looked at, the result waits and the first tab the user returns to shows it.
 *
 * The factory takes its storage, lock, focus test and clock as parameters, so the agreement can be
 * tested without a browser; `startFocusedTabResultRouter` wires the real ones.
 */

/** The part of `Storage` the router uses. */
export interface ResultRouterStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

/** Runs `run` while holding an exclusive lock on `name`, or reports that the lock was taken. */
export type ResultRouterLock = <T>(name: string, run: () => T | Promise<T>) => Promise<{ acquired: boolean; value?: T }>;

/** One upload result, as a receiving tab offers it. */
export interface RoutedResult {
  /** Identical in every tab for the same result — stamped by the server before its fan-out. */
  eventId: string;
  /** Which presenter shows it. */
  kind: string;
  /** The socket payload, handed back unchanged to the presenter. */
  payload: unknown;
}

export interface FocusedTabResultRouterDeps {
  storage: ResultRouterStorage;
  withLock: ResultRouterLock;
  /** True while this tab is the one the user is looking at. */
  isFocused: () => boolean;
  now: () => number;
  /** Shows a result in this tab. */
  present: (kind: string, payload: unknown) => void;
}

export interface FocusedTabResultRouter {
  /** Queues a result this tab received, then shows it here if this is the tab being looked at. */
  offer: (result: RoutedResult) => Promise<void>;
  /** Shows every queued result no tab has shown yet, if this is the tab being looked at. */
  presentPending: () => Promise<void>;
}

interface StoredResult {
  kind: string;
  payload: unknown;
  expiresAt: number;
  handled: boolean;
}

/** One `localStorage` key per result, so two tabs writing different results never overwrite each other. */
const RESULT_KEY_PREFIX = 'plm:rpUploadResult:v1:';

/** How long an unshown result waits — the lifetime of the signed error-sheet link it may carry. */
const RESULT_TTL_MS = 60 * 60 * 1000;

/**
 * Creates the router over the given storage, lock, focus test and clock.
 * @param deps - What the router reads and writes.
 * @returns `offer` and `presentPending`.
 */
export function createFocusedTabResultRouter(deps: FocusedTabResultRouterDeps): FocusedTabResultRouter {
  const read = (key: string): StoredResult | null => {
    try {
      const raw = deps.storage.getItem(key);
      return raw ? (JSON.parse(raw) as StoredResult) : null;
    } catch {
      return null;
    }
  };

  const write = (key: string, value: StoredResult): void => {
    try {
      deps.storage.setItem(key, JSON.stringify(value));
    } catch {
      /** Storage full or blocked: the result simply is not shared, never an error for the user. */
    }
  };

  const resultKeys = (): string[] => {
    const keys: string[] = [];
    for (let index = 0; index < deps.storage.length; index += 1) {
      const key = deps.storage.key(index);
      if (key && key.startsWith(RESULT_KEY_PREFIX)) keys.push(key);
    }
    return keys;
  };

  const dropExpired = (): void => {
    const now = deps.now();
    resultKeys().forEach((key) => {
      const entry = read(key);
      if (!entry || entry.expiresAt <= now) deps.storage.removeItem(key);
    });
  };

  /**
   * Marks a queued result as shown and returns it, or null when another tab got there first. The
   * "handled" flag is re-read inside the lock, so a tab that loses the race never shows it.
   */
  const claim = async (key: string): Promise<StoredResult | null> => {
    const outcome = await deps.withLock(key, () => {
      const entry = read(key);
      if (!entry || entry.handled || entry.expiresAt <= deps.now()) return null;
      write(key, { ...entry, handled: true });
      return entry;
    });
    return outcome.acquired ? outcome.value ?? null : null;
  };

  const presentPending = async (): Promise<void> => {
    dropExpired();
    if (!deps.isFocused()) return;
    for (const key of resultKeys()) {
      const entry = read(key);
      if (!entry || entry.handled) continue;
      const claimed = await claim(key);
      if (claimed) deps.present(claimed.kind, claimed.payload);
    }
  };

  const offer = async (result: RoutedResult): Promise<void> => {
    const key = `${RESULT_KEY_PREFIX}${result.eventId}`;
    if (!read(key)) {
      write(key, { kind: result.kind, payload: result.payload, expiresAt: deps.now() + RESULT_TTL_MS, handled: false });
    }
    await presentPending();
  };

  return { offer, presentPending };
}

/**
 * The id every tab uses for one result.
 *
 * The server stamps `eventId` once before its fan-out. An older server stamps none; the fallback is a
 * hash of the event name and payload, which is identical in every tab because every tab received the
 * same payload.
 * @param eventName - The socket event the result arrived on.
 * @param payload - The socket payload.
 * @returns The shared id.
 */
export function resolveResultEventId(eventName: string, payload: unknown): string {
  const stamped = (payload as { eventId?: unknown } | null | undefined)?.eventId;
  if (typeof stamped === 'string' && stamped) return stamped;
  return `fallback:${eventName}:${hashString(safeStringify(payload))}`;
}

/**
 * Starts the router in this browser tab: `localStorage`, Web Locks, and the tab's real focus. A queued
 * result is re-checked whenever the tab gains focus, becomes visible, or another tab queues one.
 * @param present - Shows a result in this tab.
 * @returns `offer` for the socket handlers, and `stop` to remove the listeners.
 */
export function startFocusedTabResultRouter(present: (kind: string, payload: unknown) => void): {
  offer: (result: RoutedResult) => Promise<void>;
  stop: () => void;
} {
  const router = createFocusedTabResultRouter({
    storage: browserStorage(),
    withLock: browserLock(),
    isFocused: () => document.visibilityState === 'visible' && document.hasFocus(),
    now: () => Date.now(),
    present,
  });

  const recheck = (): void => {
    router.presentPending().catch(() => undefined);
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.key && event.key.startsWith(RESULT_KEY_PREFIX)) recheck();
  };

  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', recheck);
  window.addEventListener('storage', onStorage);
  recheck();

  return {
    offer: router.offer,
    stop: () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('storage', onStorage);
    },
  };
}

/**
 * `localStorage`, or an in-memory stand-in when the browser blocks it — tabs then no longer share the
 * queue, but each still shows a result only while it is the one being looked at.
 */
function browserStorage(): ResultRouterStorage {
  try {
    const probeKey = `${RESULT_KEY_PREFIX}probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    const memory = new Map<string, string>();
    return {
      getItem: (key) => (memory.has(key) ? (memory.get(key) as string) : null),
      setItem: (key, value) => {
        memory.set(key, value);
      },
      removeItem: (key) => {
        memory.delete(key);
      },
      key: (index) => Array.from(memory.keys())[index] ?? null,
      get length() {
        return memory.size;
      },
    };
  }
}

/**
 * Web Locks with `ifAvailable`, the pattern `downloadErrorSheet` in `socket.js` already uses. Without
 * Web Locks the claim still re-reads the "handled" flag, which leaves only a millisecond-wide race.
 */
function browserLock(): ResultRouterLock {
  const locks = (navigator as Navigator & {
    locks?: { request: (name: string, options: { ifAvailable: boolean }, run: (lock: unknown) => unknown) => Promise<unknown> };
  }).locks;
  if (!locks?.request) {
    return async (_name, run) => ({ acquired: true, value: await run() });
  }
  return async (name, run) =>
    (await locks.request(name, { ifAvailable: true }, async (lock) =>
      lock ? { acquired: true, value: await run() } : { acquired: false },
    )) as { acquired: boolean; value?: Awaited<ReturnType<typeof run>> };
}

/** JSON of a payload, or an empty string when it cannot be serialised. */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '';
  }
}

/** FNV-1a 32-bit hash, as hex — stable across tabs and browsers. */
function hashString(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
}
